// Shared phrase-shaped playback engine.
// Used by Studio (main playback + single-word replay) and Library.
import { PRESETS, PEDAL_TAIL_MULTIPLIER, stepMsForBpm, voiceForPreset } from "@/lib/presets";
import { playPad, playPitch, playPiano, playHarp } from "@/lib/synth";

const rand = (lo, hi) => lo + Math.random() * (hi - lo);

function voiceFn(voice) {
  if (voice === "piano") return playPiano;
  if (voice === "harp") return playHarp;
  return voice === "pad" ? playPad : playPitch;
}

/**
 * Play a sequence of {note, octave, pitch, wordIdx, charIdx, letter} entries
 * with phrase-shaped timing + velocity. Returns once playback finishes (or
 * shouldStop() returns true).
 *
 * options:
 *   presetKey   — key from PRESETS, defaults to "meditation"
 *   voice       — "pad" (default) or "tone"
 *   pedal       — boolean, multiplies ring tail by PEDAL_TAIL_MULTIPLIER
 *   shouldStop  — () => boolean, polled before each note + between words
 *   onTick      — (i, entry, stepMs) => void, fired right before a note plays
 */
export async function playSequenceAudio(sequence, options = {}) {
  if (!sequence || sequence.length === 0) return;
  const {
    presetKey = "meditation",
    pedal = false,
    voice = null,
    shouldStop = () => false,
    onTick = null,
    bpm = null,
    sustain = null,
    measured = false,
  } = options;

  const preset = PRESETS[presetKey] || PRESETS.meditation;
  // `bpm` and `sustain` may be numbers or getters. Getters are re-read before
  // every note, so moving a slider mid-playback lands on the very next note
  // instead of waiting for the next Play.
  const read = (v) => (typeof v === "function" ? v() : v);

  // A BPM from the studio replaces the preset's own step, and the rest between
  // words stretches with it so the feel holds together at any tempo. Sustain
  // scales how long each note rings — the real reason notes overlap — and the
  // studio moves the room with it.
  const tune = () => {
    let tuned = preset;
    const liveBpm = read(bpm);
    if (liveBpm) {
      const step = stepMsForBpm(presetKey, liveBpm);
      const ratio = step / preset.baseStepMs;
      tuned = { ...tuned, baseStepMs: step, wordRestMs: (preset.wordRestMs || 0) * ratio };
    }
    const liveSustain = read(sustain);
    if (liveSustain != null) {
      const k = Math.min(2, Math.max(0, Number(liveSustain) / 100));
      tuned = {
        ...tuned,
        interiorTailSec: preset.interiorTailSec * k,
        finalTailSec: preset.finalTailSec * k,
      };
    }
    return tuned;
  };
  let p = tune();
  const play = voiceFn(voice || voiceForPreset(presetKey));
  const tailMult = pedal ? (p.pedalTailMult ?? PEDAL_TAIL_MULTIPLIER) : 1;

  const wordLens = sequence.reduce((acc, e) => {
    acc[e.wordIdx] = (acc[e.wordIdx] || 0) + 1;
    return acc;
  }, {});
  const posCounters = {};
  let nextHoldMult = 1;

  for (let i = 0; i < sequence.length; i += 1) {
    if (shouldStop()) break;
    p = tune();  // pick up any live tempo/sustain change for this note
    const entry = sequence[i];
    const next = sequence[i + 1];
    const isLastInWord = !next || next.wordIdx !== entry.wordIdx;

    posCounters[entry.wordIdx] = posCounters[entry.wordIdx] || 0;
    const posInWord = posCounters[entry.wordIdx];
    posCounters[entry.wordIdx] += 1;
    const wordLen = wordLens[entry.wordIdx];
    const isFirstInWord = posInWord === 0;
    const isSecondToLast = !isLastInWord && wordLen >= 3 && posInWord === wordLen - 2;
    const isInterior = !isFirstInWord && !isLastInWord && !isSecondToLast;

    let stepMs = p.baseStepMs + Math.random() * p.stepJitterMs;
    // Swung eighths: the note on the beat gets the long half of the pair.
    if (p.swing) {
      stepMs *= (i % 2 === 0 ? p.swing : 1 - p.swing) * 2;
    }
    if (measured) {
      stepMs = p.baseStepMs; // no jitter, no swing — the bar keeps the time
      // Straight eighths: the bar structure does the phrasing, so the
      // humanising multipliers and word rests step aside.
      if (entry.rest) {
        if (onTick) onTick(i, entry, stepMs, 0, 0);
        await new Promise((r) => setTimeout(r, stepMs));
        continue;
      }
      const tail = (isLastInWord ? p.finalTailSec : p.interiorTailSec) * tailMult;
      const vel = rand(p.velBodyMin, p.velBodyMin + p.velBodyRange);
      const dur = stepMs / 1000 + tail;
      if (onTick) onTick(i, entry, stepMs, vel, dur);
      play(entry.pitch, dur, vel);
      await new Promise((r) => setTimeout(r, stepMs));
      continue;
    }

    if (isFirstInWord && wordLen > 1) stepMs *= p.firstLetterMult;
    if (isSecondToLast) stepMs *= p.secondToLastMult;
    if (isLastInWord) stepMs *= p.lastLetterMult;

    stepMs *= nextHoldMult;
    nextHoldMult = 1;

    let appliedGrace = false;
    if (isInterior && wordLen >= 4 && Math.random() < p.graceChance) {
      stepMs *= p.graceShortMult;
      nextHoldMult = p.graceNextMult;
      appliedGrace = true;
    }

    let velocity;
    if (isFirstInWord && wordLen > 1) {
      velocity = rand(p.velFirst[0], p.velFirst[1]);
    } else if (isLastInWord) {
      velocity = rand(p.velLast[0], p.velLast[1]);
    } else {
      const mid = (wordLen - 1) / 2;
      const distFromMid = Math.abs(posInWord - mid) / Math.max(1, mid);
      velocity = p.velBodyMin + distFromMid * p.velBodyRange + Math.random() * 0.08;
      if (appliedGrace) velocity *= 0.78;
    }

    const baseTailSec = isLastInWord ? p.finalTailSec : p.interiorTailSec;
    const noteSec = stepMs / 1000 + baseTailSec * tailMult;
    if (onTick) onTick(i, entry, stepMs, velocity, noteSec);
    play(entry.pitch, noteSec, velocity);

    await new Promise((r) => setTimeout(r, stepMs));

    if (isLastInWord && next && !shouldStop()) {
      await new Promise((r) => setTimeout(r, p.wordRestMs));
    }
  }
}

/**
 * Verbatim replay of a recorded remix. Each event is either:
 *   { kind: 'note', pitch, stepMs, noteSec, velocity, ...staffMeta }
 *   { kind: 'rest', durMs }
 *
 * The original timing (note lengths + inter-tap rests) is preserved exactly.
 *
 * options:
 *   shouldStop   — () => boolean
 *   onNote       — (event) => void, fires right before a note plays
 */
export async function playEventsExact(events, options = {}) {
  if (!events || events.length === 0) return;
  const { shouldStop = () => false, onNote = null, voice = "pad" } = options;
  const play = voiceFn(voice);

  for (let i = 0; i < events.length; i += 1) {
    if (shouldStop()) break;
    const ev = events[i];
    if (ev.kind === "rest") {
      await new Promise((r) => setTimeout(r, Math.max(0, ev.durMs || 0)));
      continue;
    }
    if (ev.kind === "note") {
      if (onNote) onNote(ev);
      play(ev.pitch, ev.noteSec || ev.stepMs / 1000 + 3.0, ev.velocity ?? 0.5);
      await new Promise((r) => setTimeout(r, Math.max(0, ev.stepMs || 200)));
    }
  }
}
